import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  CampaignApiRecord,
  CampaignMutationResponse,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { mergeCampaignIntoLists } from "./campaignQueryCache";
import { unwrapGamePassEntity } from "./responseMappers";

const withCampaignId = (route: string, campaignId: string): string =>
  route.replace("{campaign_id}", campaignId);

/**
 * Factory for the body-less campaign lifecycle transitions (schedule,
 * unschedule, complete, cancel). Each POSTs to its route with an empty body,
 * unwraps the echoed record, and merges it back into every cached listing —
 * the exact shape the season transition hooks (`useScheduleSeason`, etc.) use,
 * consolidated here since the five transitions differ only by route + key.
 */
const createCampaignTransitionHook =
  (
    route: string,
    action: string,
  ): (() => UseMutationResult<CampaignApiRecord, Error, string>) =>
  () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: ["game-pass", "campaigns", action],
      mutationFn: async (campaignId: string) => {
        const response = await getPrivateInstance().post<CampaignMutationResponse>(
          withCampaignId(route, campaignId),
          {},
        );
        return unwrapGamePassEntity(response.data, "campaign");
      },
      onSuccess: (campaign) => mergeCampaignIntoLists(queryClient, campaign),
    });
  };

export const useScheduleCampaign = createCampaignTransitionHook(
  API_ROUTES.SCHEDULE_CAMPAIGN,
  "schedule",
);

export const useUnscheduleCampaign = createCampaignTransitionHook(
  API_ROUTES.UNSCHEDULE_CAMPAIGN,
  "unschedule",
);

export const useCompleteCampaign = createCampaignTransitionHook(
  API_ROUTES.COMPLETE_CAMPAIGN,
  "complete",
);

export const useCancelCampaign = createCampaignTransitionHook(
  API_ROUTES.CANCEL_CAMPAIGN,
  "cancel",
);

export type ActivateCampaignVariables = {
  campaignId: string;
  /** Force activation past soft warnings (mirrors `useActivateSeason`). */
  force?: boolean;
};

/**
 * Activate a campaign (`POST .../activate`). Kept separate from the body-less
 * transitions because it forwards an optional `{ force }` body.
 */
export const useActivateCampaign = (): UseMutationResult<
  CampaignApiRecord,
  Error,
  ActivateCampaignVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "campaigns", "activate"],
    mutationFn: async ({ campaignId, force = false }) => {
      const response = await getPrivateInstance().post<CampaignMutationResponse>(
        withCampaignId(API_ROUTES.ACTIVATE_CAMPAIGN, campaignId),
        force ? { force: true } : {},
      );
      return unwrapGamePassEntity(response.data, "campaign");
    },
    onSuccess: (campaign) => mergeCampaignIntoLists(queryClient, campaign),
  });
};
